/* istanbul ignore file */
import React from 'react';
import { ShowSection, SectionRow, BigSectionRow, ShowPageLayout } from 'framework/ui/layout';
import { BooleanField, DateField, TextField, HtmlField } from 'framework/ui/field';
import AssetHeader from 'domain/finance/assets/shared/AssetHeader';
import SingleAssetBreadcrumbs from 'domain/finance/assets/shared/SingleAssetBreadcrumbs';
import AssetCategoryField from 'domain/finance/assets/shared/field/AssetCategoryField';
import AssetCategoryTypeField from 'domain/finance/assets/shared/field/AssetCategoryTypeField';
import AssetConditionField from 'domain/finance/assets/shared/field/AssetConditionField';
import AssetDurationField from 'domain/finance/assets/shared/field/AssetDurationField';
import { LedgerLink } from 'domain/shared/ledger';
import CopyAssetButton from 'domain/finance/assets/shared/button/CopyAssetButton';
import { InvoiceTotalField } from 'domain/shared/core/invoice';
import AssetStatusField from 'domain/finance/assets/shared/field/AssetStatusField';
import {
  ASSET_STATUS,
  isAutoRenewSubscription as initialIsAutoRenewSubscription,
  isExpandedEquipmentCategory as initialIsExpandedEquipmentCategory,
  isIntangibleAsset as initialIsIntangibleAsset,
  isSubscription as initialIsSubscription,
} from 'domain/finance/assets/useAssetService';
import { BUSINESS_OBJECT_TYPES } from 'domain/finance/ledger/useLedgerService';
import TransfersListField from 'domain/finance/shared/field/TransfersListField';
import { useAssetId } from 'domain/finance/assets/shared/useAssetRouting';
import { Box } from '@mui/material';
import { DATE_FORMAT } from 'framework/utils';
import AssignAssetOwnerButton from 'domain/finance/assets/shared/button/AssignAssetOwnerButton';
import ReturnToStorageAssetButton from 'domain/finance/assets/shared/button/ReturnToStorageAssetButton';
import EditAssetButton from 'domain/finance/assets/shared/button/EditAssetButton';
import AssetMenu from 'domain/finance/assets/shared/AssetMenu';
import AllNotesButton from '../shared/button/AllNotesButton';
import DisposeAssetButton from '../shared/button/DisposeAssetButton';
import WriteOffAssetButton from '../shared/button/WriteOffAssetButton';
import { EmployeeLinkFieldWithNameLoading, OfficeCodeField } from 'domain/shared/employee';

const isExpandedEquipmentCategory = (data: any) =>
  initialIsExpandedEquipmentCategory(data.getValueBySource('category'));

const isIntangibleAsset = (data: any) => initialIsIntangibleAsset(data.getValueBySource('category'));

const isSubscription = (data: any) => initialIsSubscription(data.getValueBySource('type'));

const isAutoRenewSubscription = (data: any) =>
  initialIsAutoRenewSubscription(data.getValueBySource('subscriptionDetails'));

const ViewAssetPageContent = ({
  isSensitiveDataLoading = false,
  isSensitiveDataIsShown = false,
  toggleSensitiveDataButton = <></>,
}: {
  isSensitiveDataLoading?: boolean;
  isSensitiveDataIsShown?: boolean;
  toggleSensitiveDataButton?: React.ReactElement;
}) => {
  const id = useAssetId();

  return (
    <ShowPageLayout
      entityHeader={<AssetHeader />}
      breadcrumbs={<SingleAssetBreadcrumbs />}
      headerButtons={[
        toggleSensitiveDataButton,
        <EditAssetButton isVisible={(data: any) => ![ASSET_STATUS.DISPOSED].includes(data?.status)} />,
        <CopyAssetButton source="id" />,
        <WriteOffAssetButton />,
        <DisposeAssetButton />,
      ]}
      hasSeparateSections
      menu={<AssetMenu />}
    >
      <ShowSection title="Asset details">
        <SectionRow>
          <TextField source="inventoryNumber" label="Inventory number" />
          <AssetCategoryField source="category" label="Category" placeholder="Select Category" />
          <AssetCategoryTypeField source="type" label="Type" categorySource="category" />
          <OfficeCodeField source="office" />
        </SectionRow>

        <SectionRow>
          <TextField source="name" label="Name" />
          <TextField source="serialNumber" label="Serial number" />
          <AssetConditionField source="condition" label="Condition" isVisible={isExpandedEquipmentCategory} />
          <TextField source="location" />
        </SectionRow>

        <BigSectionRow>
          <TextField source="characteristics" />
        </BigSectionRow>
      </ShowSection>

      <ShowSection title="Financial information">
        <SectionRow>
          <LedgerLink source="attribution.balanceAccountId" nameSource="balanceAccountId" label="Balance account" />
          <LedgerLink source="attribution.expenseAccountId" nameSource="expenseAccountId" label="Expense account" />
          <TextField source="attribution.budgetCategory" nameSource="budgetCategory" label="Budget category" />
          <AssetStatusField source="status" label="Status" />
        </SectionRow>

        <SectionRow>
          <InvoiceTotalField
            source="costInPurchaseCurrency.amount"
            currencySource="costInPurchaseCurrency.currency"
            label="Purshase cost"
            isVisible={isSensitiveDataIsShown}
          />
          <InvoiceTotalField
            source="costInStandardOperationCurrency.amount"
            currencySource="costInStandardOperationCurrency.currency"
            label="Cost in target currency"
            isVisible={isSensitiveDataIsShown}
          />
          <AssetDurationField
            source="usefulLifePeriod.amount"
            timeUnitSource="usefulLifePeriod.timeUnit"
            label="Useful life"
          />
        </SectionRow>

        <SectionRow>
          <DateField label="Purchase date" source="purchaseDate" />
          <DateField label="Validity start date" source="validityStartDate" isVisible={isIntangibleAsset} />
          <DateField label="Validity end date" source="validityEndDate" isVisible={isIntangibleAsset} />
          <DateField label="Warranty end date" source="warrantyEndDate" isVisible={isExpandedEquipmentCategory} />
          <DateField
            dateFormat={DATE_FORMAT.SIMPLE_MONTH}
            source="latestAccountingCycle"
            label="Latest amortization cycle"
          />
        </SectionRow>
      </ShowSection>

      <ShowSection title="Subscription details" isVisible={isSubscription}>
        <SectionRow>
          <BooleanField source="subscriptionDetails.isAutoRenew" label="Is auto-renew?" />
          <DateField
            label="Next payment date"
            source="subscriptionDetails.nextPaymentDate"
            isVisible={isAutoRenewSubscription}
          />
          <DateField
            label="Expiry date"
            source="subscriptionDetails.expiryDate"
            isVisible={(data: any) => !isAutoRenewSubscription(data)}
          />
          <AssetDurationField
            source="subscriptionDetails.billingCycle.amount"
            timeUnitSource="subscriptionDetails.billingCycle.timeUnit"
            label="Billing cycle"
            prefix="Every "
          />
        </SectionRow>
      </ShowSection>

      <ShowSection title="Assignment details" actions={[<AssignAssetOwnerButton />, <ReturnToStorageAssetButton />]}>
        <SectionRow>
          <EmployeeLinkFieldWithNameLoading
            source="assignmentDetails.currentOwner"
            nameSource="assignmentDetails.currentOwnerFullName"
            label="Current owner"
          />
          <DateField
            source="assignmentDetails.provisionDate"
            label="Provision date"
            isVisible={(data: any) => !!data.getValueBySource('assignmentDetails')?.currentOwner}
          />
        </SectionRow>
      </ShowSection>

      <ShowSection title="Note" actions={[<AllNotesButton />]}>
        <BigSectionRow>
          <Box sx={{ '.field__wrapper > h3': { display: 'none' } }}>
            <HtmlField source="note.content" label="Note" />
          </Box>
        </BigSectionRow>
      </ShowSection>

      <ShowSection isVisible={isSensitiveDataIsShown || isSensitiveDataLoading}>
        <TransfersListField
          businessObjectId={id}
          businessObjectType={BUSINESS_OBJECT_TYPES.ASSET}
          startDateSource="purchaseDate"
        />
      </ShowSection>
    </ShowPageLayout>
  );
};

export default ViewAssetPageContent;
